import React, { useState } from 'react';
import { PITCH_TYPES } from '../../hooks/useGameState';

const BIP_OUTCOMES = ['Single', 'Double', 'Triple', 'Home Run', 'Out', 'Error', 'Field Error', 'Fielder\'s Choice'];
const STRIKE_DETAILS = ['swinging', 'looking', 'foul', 'foulTip'];

const NON_PITCH_TYPES = ['adjustRuns', 'adjustEarnedRuns', 'fcOut', 'advancementOut', 'pickoff', 'cs', 'sb', 'balk', 'inheritedRunnerScored'];

function isAtBatEnd(action) {
  if (!action) return false;
  if (action.type === 'ballInPlay' || action.type === 'ibb' || action.type === 'hbp') return true;
  if (action.type === 'd3k_reached' || action.type === 'd3k_out') return true;
  return false;
}

function isNonPitch(action) {
  return NON_PITCH_TYPES.includes(action.type);
}

function groupIntoBatters(actions) {
  const innings = {};
  let balls = 0;
  let strikes = 0;
  let batterNum = 1;
  let currentBatter = [];
  let currentInning = actions.length > 0 ? actions[0].inning : 1;

  for (let i = 0; i < actions.length; i++) {
    const a = actions[i];

    // Inning change — flush current batter and reset
    if (a.inning !== undefined && a.inning !== currentInning) {
      if (currentBatter.length > 0) {
        if (!innings[currentInning]) innings[currentInning] = [];
        innings[currentInning].push({ batterNum, pitches: currentBatter });
      }
      currentInning = a.inning;
      batterNum = 1;
      currentBatter = [];
      balls = 0;
      strikes = 0;
    }

    // Non-pitch events attach to current batter but don't affect count
    currentBatter.push(a);
    if (isNonPitch(a)) continue;

    // Update count
    if (a.type === 'ball') {
      balls++;
      if (balls >= 4) {
        // Walk — end at-bat
        if (!innings[currentInning]) innings[currentInning] = [];
        innings[currentInning].push({ batterNum, pitches: currentBatter });
        batterNum++;
        currentBatter = [];
        balls = 0;
        strikes = 0;
      }
    } else if (a.type === 'strike') {
      if (a.detail !== 'foul' && a.detail !== 'foulTip') {
        strikes++;
      } else if (strikes < 2) {
        strikes++;
      }
      if (strikes >= 3) {
        // Strikeout — end at-bat
        if (!innings[currentInning]) innings[currentInning] = [];
        innings[currentInning].push({ batterNum, pitches: currentBatter });
        batterNum++;
        currentBatter = [];
        balls = 0;
        strikes = 0;
      }
    } else if (isAtBatEnd(a)) {
      if (!innings[currentInning]) innings[currentInning] = [];
      innings[currentInning].push({ batterNum, pitches: currentBatter });
      batterNum++;
      currentBatter = [];
      balls = 0;
      strikes = 0;
    }
  }

  // Flush remaining
  if (currentBatter.length > 0) {
    if (!innings[currentInning]) innings[currentInning] = [];
    innings[currentInning].push({ batterNum, pitches: currentBatter });
  }

  return innings;
}

function describeAction(action) {
  const pitch = action.pitchType ? `${action.pitchType} · ` : '';
  const vel = action.velocity ? ` ${action.velocity} MPH` : '';
  switch (action.type) {
    case 'strike':
      return `${pitch}Strike — ${action.detail || ''}${vel}`;
    case 'ball':
      return `${pitch}Ball${vel}`;
    case 'wildPitch':
      return `${pitch}Wild Pitch${vel}`;
    case 'ballInPlay': {
      const ht = action.hitType ? ` (${action.hitType})` : '';
      return `${pitch}In Play — ${action.detail || ''}${ht}${vel}`;
    }
    case 'hbp': return 'Hit By Pitch';
    case 'ibb': return 'Intentional Walk';
    case 'balk': return 'Balk';
    case 'pickoff': return `Pickoff${action.result ? ' — ' + action.result : ''}`;
    case 'adjustRuns': return `Run Scored (Unearned)`;
    case 'adjustEarnedRuns': return `Run Scored (Earned)`;
    case 'fcOut': return `Fielder\'s Choice — out`;
    case 'advancementOut': return `Runner Out — advancing`;
    default: return action.type;
  }
}

function EditPitchModal({ action, onSave, onDelete, onClose, trackPitchTypes = false, trackVelocity = false }) {
  const [type, setType] = useState(action.type);
  const [detail, setDetail] = useState(action.detail || '');
  const [pitchType, setPitchType] = useState(action.pitchType || '');
  const [velocity, setVelocity] = useState(action.velocity || '');

  function handleSave() {
    const changes = { type };
    if (detail) changes.detail = detail;
    if (pitchType) changes.pitchType = pitchType;
    if (velocity) changes.velocity = parseInt(velocity);
    onSave(action.id, changes);
    onClose();
  }

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fld-modal-title">Edit Pitch</div>

        <div style={{ marginBottom: 12 }}>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Result</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['strike', 'ball', 'ballInPlay', 'wildPitch', 'hbp', 'ibb', 'balk'].map(t => (
              <button
                key={t}
                className="fld-modal-confirm"
                style={{ flex: 'none', padding: '6px 12px', fontSize: 12, background: type === t ? 'var(--amber)' : 'var(--surface-3)', color: type === t ? '#000' : 'var(--text-2)' }}
                onClick={() => setType(t)}
              >
                {t === 'ballInPlay' ? 'In Play' : t === 'wildPitch' ? 'Wild Pitch' : t === 'hbp' ? 'HBP' : t === 'ibb' ? 'IBB' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {type === 'strike' && (
          <div style={{ marginBottom: 12 }}>
            <div className="fld-modal-body" style={{ marginBottom: 6 }}>Strike Type</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {STRIKE_DETAILS.map(d => (
                <button
                  key={d}
                  className="fld-modal-confirm"
                  style={{ flex: 'none', padding: '6px 12px', fontSize: 12, background: detail === d ? 'var(--amber)' : 'var(--surface-3)', color: detail === d ? '#000' : 'var(--text-2)' }}
                  onClick={() => setDetail(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {type === 'ballInPlay' && (
          <div style={{ marginBottom: 12 }}>
            <div className="fld-modal-body" style={{ marginBottom: 6 }}>Outcome</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {BIP_OUTCOMES.map(d => (
                <button
                  key={d}
                  className="fld-modal-confirm"
                  style={{ flex: 'none', padding: '6px 12px', fontSize: 12, background: detail === d.toLowerCase() ? 'var(--amber)' : 'var(--surface-3)', color: detail === d.toLowerCase() ? '#000' : 'var(--text-2)' }}
                  onClick={() => setDetail(d.toLowerCase())}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {trackPitchTypes && (
          <div style={{ marginBottom: 12 }}>
            <div className="fld-modal-body" style={{ marginBottom: 6 }}>Pitch Type</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                className="fld-modal-confirm"
                style={{ flex: 'none', padding: '6px 12px', fontSize: 12, background: !pitchType ? 'var(--amber)' : 'var(--surface-3)', color: !pitchType ? '#000' : 'var(--text-2)' }}
                onClick={() => setPitchType('')}
              >
                None
              </button>
              {PITCH_TYPES.map(pt => (
                <button
                  key={pt}
                  className="fld-modal-confirm"
                  style={{ flex: 'none', padding: '6px 12px', fontSize: 12, background: pitchType === pt ? 'var(--amber)' : 'var(--surface-3)', color: pitchType === pt ? '#000' : 'var(--text-2)' }}
                  onClick={() => setPitchType(pt)}
                >
                  {pt}
                </button>
              ))}
            </div>
          </div>
        )}

        {trackVelocity && (
          <div style={{ marginBottom: 16 }}>
            <div className="fld-modal-body" style={{ marginBottom: 6 }}>Velocity</div>
            <input
              type="number"
              value={velocity}
              onChange={e => setVelocity(e.target.value)}
              placeholder="e.g. 72"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 14 }}
            />
          </div>
        )}

        <button className="fld-modal-confirm" style={{ width: '100%', marginBottom: 8 }} onClick={handleSave}>
          Save Changes
        </button>
        <button
          className="fld-modal-confirm"
          style={{ width: '100%', marginBottom: 8, background: 'rgba(248,113,113,0.15)', color: 'var(--red)', border: '1px solid rgba(248,113,113,0.4)' }}
          onClick={() => { onDelete(action.id); onClose(); }}
        >
          Delete Pitch
        </button>
        <button className="fld-modal-cancel" style={{ width: '100%' }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default function PitchLogModal({ isOpen, onClose, actions = [], onEdit, onDelete, trackPitchTypes = false, trackVelocity = false }) {
  const [editingAction, setEditingAction] = useState(null);

  if (!isOpen) return null;

  const grouped = groupIntoBatters(actions);
  const innings = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div
        className="fld-modal-card"
        style={{ maxHeight: '85vh', overflowY: 'auto', maxWidth: 420, width: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="fld-modal-title" style={{ margin: 0 }}>Pitch Log</div>
          <button className="fld-modal-cancel" style={{ border: 'none', padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>

        {innings.length === 0 && (
          <div className="fld-modal-body" style={{ textAlign: 'center', padding: '20px 0' }}>No pitches recorded yet.</div>
        )}

        {innings.map(inning => (
          <div key={inning} style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.1em',
              color: 'var(--amber)',
              textTransform: 'uppercase',
              marginBottom: 10,
              paddingBottom: 4,
              borderBottom: '1px solid var(--border)',
            }}>
              Inning {inning}
            </div>

            {grouped[inning].map(({ batterNum, pitches }) => (
              <div key={batterNum} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
                  Batter {batterNum}
                </div>
                {pitches.map(pitch => (
                  <div
                    key={pitch.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderRadius: 6,
                      marginBottom: 4,
                      background: 'var(--surface-2)',
                      cursor: 'pointer',
                    }}
                    onClick={() => setEditingAction(pitch)}
                  >
                    <span style={{ fontSize: 13, color: 'var(--text)' }}>{describeAction(pitch)}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8, flexShrink: 0 }}>✎</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {editingAction && (
        <EditPitchModal
          action={editingAction}
          onSave={onEdit}
          onDelete={onDelete}
          onClose={() => setEditingAction(null)}
          trackPitchTypes={trackPitchTypes}
          trackVelocity={trackVelocity}
        />
      )}
    </div>
  );
}
